// ===============================================
// File: src/components/Widgets/DailyTodoWidget.jsx
// Fix: theme-aware inputs/buttons for light & dark
// - Inputs/buttons use light defaults + dark: overrides
// - No changes to logic, layout, or copy
// ===============================================

import React, { useEffect, useMemo, useRef, useState } from "react";
import WidgetHeader from "../Common/WidgetHeader";
import { usePlanner } from "../../contexts/PlannerContext";
import { useFeedback } from "../../contexts/FeedbackContext";
import { useTasks } from "../../contexts/TasksContext";

const uid = () =>
  globalThis.crypto?.randomUUID?.() ||
  `todo_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

export default function DailyTodoWidget({ backTo = "/dashboard", defaultPlannerMinutes = 60 }) {
  const { upsertEvents } = usePlanner() || {};
  const { play, buzz } = useFeedback?.() || { play: () => {}, buzz: () => {} };

  // unified tasks context (today-scoped)
  const { todayList = [], add, toggle, remove, markAddedToPlanner } = useTasks();

  const [q, setQ] = useState("");
  const [adding, setAdding] = useState("");
  const [toast, setToast] = useState(null); // {msg, ts}
  const hideToastRef = useRef(null);

  // theme-aware classes
  const inputCls =
    "flex-1 px-3 py-2 rounded text-sm border bg-white/80 border-black/10 text-black placeholder-black/50 " +
    "dark:bg-zinc-800 dark:border-white/10 dark:text-white dark:placeholder-white/60";

  const secondaryBtnCls =
    "px-2 py-2 rounded text-sm bg-black/5 text-black border border-black/10 hover:bg-black/10 " +
    "dark:bg-zinc-700 dark:text-white dark:border-white/10";

  const listItemCls =
    "flex items-start gap-2 p-2 rounded border bg-black/5 border-black/10 " +
    "dark:bg-white/5 dark:border-white/10";

  // toast helpers
  const showToast = (msg) => {
    setToast({ msg, ts: Date.now() });
    clearTimeout(hideToastRef.current);
    hideToastRef.current = setTimeout(() => setToast(null), 1400);
  };
  useEffect(() => () => clearTimeout(hideToastRef.current), []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (todayList || []).filter((t) =>
      !needle ? true : (t.title || "").toLowerCase().includes(needle)
    );
  }, [todayList, q]);

  const addTodo = () => {
    const title = adding.trim();
    if (!title) return;
    add(title);
    setAdding("");
    play("success");
    buzz("light");
    showToast("Task added");
  };

  const toggleDone = (id) => {
    const cur = todayList.find((t) => String(t.id) === String(id));
    const wasIncomplete = !!cur && !cur.done;

    toggle(id);
    play("tap");
    buzz("light");
  if (wasIncomplete) showToast("Task completed");
  };

  const handleRemove = (id) => {
    if (!window.confirm("Delete this task?")) return;
    remove(id);
    play("stop");
    buzz("medium");
    showToast("Task deleted");
  };

  const addToPlanner = (task) => {
    if (!upsertEvents) {
      showToast("Planner not ready");
      return;
    }
    const start = new Date();
    const end = new Date(start.getTime() + (Number(defaultPlannerMinutes) || 60) * 60000);
    upsertEvents?.([
      {
        id: uid(),
        title: task.title,
        start: start.toISOString(),
        end: end.toISOString(),
        status: "todo",
        progress: 0,
        tags: ["other"], // default column
        order: 9999,
      },
    ]);
    markAddedToPlanner(task.id);
    play("success");
    buzz("success");
    showToast("Added to Planner");
    window.dispatchEvent(
      new CustomEvent("mudo:planner:taskAdded", { detail: { title: task.title } })
    );
  };

  const doneCount = filtered.filter((t) => t.done).length;

  return (
    <div className="relative rounded-lg p-0 bg-transparent text-inherit">
      <WidgetHeader
        to={backTo}
        title={
          <div className="flex items-center gap-2">
            <span role="img" aria-label="Tasks"></span>
            <span>Daily To-Do</span>
          </div>
        }
        help={
          "A quick daily checklist. Add tasks here, tick them off, and optionally send a task to the Planner in one tap."
        }
        right={<span className="text-xs opacity-70">{doneCount}/{filtered.length} done</span>}
      />

      {/* Toast (ARIA live) */}
      <div
        aria-live="polite"
        aria-atomic="true"
        className="pointer-events-none fixed z-[5]"
        style={{ inset: "auto 12px 12px auto" }}
      >
        {toast && (
          <div
            className="px-3 py-2 rounded shadow-lg text-sm"
            style={{
              background: "rgba(0,0,0,.85)",
              color: "white",
              transform: "translateY(0)",
              animation: "todoToast .18s ease-out",
            }}
          >
            {toast.msg}
          </div>
        )}
      </div>
      <style>{`
        @keyframes todoToast {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* Add row */}
      <div className="flex gap-2 mb-3">
        <input
          value={adding}
          onChange={(e) => setAdding(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") addTodo(); }}
          placeholder="Add a quick task…"
          className={inputCls}
          aria-label="New task"
        />
        <button
          onClick={addTodo}
          className="px-3 py-2 rounded text-white text-sm transition-colors"
          style={{ backgroundColor: 'var(--color-primary)' }}
        >
          Add
        </button>
      </div>

      {/* Search */}
      <div className="flex items-center gap-2 mb-3">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search today’s tasks…"
          className={inputCls}
          aria-label="Search tasks"
        />
        <button onClick={() => setQ("")} className={secondaryBtnCls}>Clear</button>
      </div>

      {/* List */}
      {filtered.length === 0 ? (
        <div className="rounded-lg border p-4 text-sm opacity-80 bg-black/5 border-black/10 dark:bg-white/5 dark:border-white/10">
          No tasks today. Add your first one above, or pull items into today from the Planner.
        </div>
      ) : (
        <ul className="space-y-2">
          {filtered.map((t) => (
            <li key={t.id} className={listItemCls}>
              <input
                type="checkbox"
                checked={!!t.done}
                onChange={() => toggleDone(t.id)}
                className="mt-1 h-4 w-4 accent-emerald-500"
                aria-label={`Toggle ${t.title}`}
              />
              <div className="min-w-0 flex-1">
                <div className={`text-sm ${t.done ? "line-through opacity-60" : ""}`}>
                  {t.title}
                </div>
                <div className="mt-1 flex gap-2">
                  <button
                    onClick={() => addToPlanner(t)}
                    className={`px-2 py-1 rounded text-xs border transition-colors ${
                      t.addedToPlanner
                        ? "bg-emerald-700/20 text-emerald-700 border-emerald-700/30 dark:bg-emerald-700/50 dark:text-emerald-200 dark:border-emerald-400/30"
                        : "bg-black/5 text-black border-black/10 hover:bg-black/10 dark:bg-white/5 dark:text-white dark:border-white/10"
                    }`}
                    title={t.addedToPlanner ? "Added" : "Add to Planner"}
                    disabled={t.addedToPlanner}
                  >
                    {t.addedToPlanner ? "In Planner" : "Add to Planner"}
                  </button>
                  <button
                    onClick={() => handleRemove(t.id)}
                    className="px-2 py-1 rounded text-xs bg-red-600/85 text-white hover:bg-red-600"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      <p className="mt-3 text-[11px] opacity-70">
        Tip: Tasks are day-scoped. Adding to Planner creates a {defaultPlannerMinutes}-minute task in the “Other” column.
      </p>
    </div>
  );
}
